/**
 * @component AutoLogout
 * @title (인터넷뱅킹) 자동로그아웃 팝업
 * @description
 *
 *  PUBLISH_ID [
 *      LGISEL009-00DG 자동로그아웃 팝업
 *  ]
 * @properties
 * @param
 * @author 2034263
 * @history
 *   - [2025-09-30] 초기 작성
 */

import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { MessageDialog } from 'scbk-ui-web';

import {
  Channel,
  sessionTimerManager,
  useChannelContext,
  useGlobalErrorStore,
  useSessionTimerStore,
} from 'scbk-services';

import { useLogout, usePageMove, usePopupStore, useSession } from 'scbk-shared-module';

export const AutoLogout: React.FC = () => {
  const { remainingTime, isExpiredSession, isAlertSession } = useSessionTimerStore();
  const { channel } = useChannelContext();

  if (channel !== Channel.IB) {
    return <></>;
  }

  if (isExpiredSession) {
    return <AutoLogoutCompleteDialog isExpiredSession={isExpiredSession} />;
  } else if (isAlertSession && remainingTime) {
    return <AutoLogoutAlertDialog remainingTime={remainingTime} />;
  }

  return <></>;
};

export const AutoLogoutAlertDialog: React.FC<{ remainingTime: number }> = memo(
  ({ remainingTime }) => {
    const { t } = useTranslation();
    const { extendSession } = useSession();
    const { doLogout } = useLogout();

    return (
      <>
        <MessageDialog
          type="confirm"
          title={t('AutoLogout.001', '자동 로그아웃 안내')}
          open={true}
          okBtnLabel={t('AutoLogout.006', '로그인 연장')}
          onConfirm={() => {
            extendSession();
            sessionTimerManager.restart(true);
          }}
          cancelBtnLabel={t('AutoLogout.005', '로그아웃')}
          onCancel={() => {
            // 자동로그아웃 완료 화면 노출
            doLogout({
              onLogout: () => {
                sessionTimerManager.expire();
              },
            });
          }}
        >
          <div className="text-center">
            <p className="body-lg text-basic-02">
              <Trans t={t} i18nKey="AutoLogout.002">
                일정시간(약 10분) 동안 이용하지
                <br />
                않아 안전한 금융거래 보호를 위해
                <br />
                자동 로그아웃 예정입니다.
              </Trans>
            </p>
            <div className="my-4 p-5 font-weight-bold heading-h5 bg-basic-background-04 rounded-lg">
              <span className="eng-heading-h1 mr-1 text-primary-04">{remainingTime}</span>
              <span className="heading-h3 text-base-black">{t('AutoLogout.003', '초')}</span>
            </div>
            <p className="eng-body-md text-basic-02">
              {t('AutoLogout.004', '자동 로그아웃까지 남은 시간')}
            </p>
          </div>
        </MessageDialog>
      </>
    );
  },
  (prev, next) => {
    return prev.remainingTime === next.remainingTime;
  },
);

const AutoLogoutCompleteDialog: React.FC<{ isExpiredSession: boolean }> = memo(
  ({ isExpiredSession }) => {
    const { t } = useTranslation();
    const { doLogout } = useLogout();
    const { goLogin } = usePageMove();
    const { clear: errorClear } = useGlobalErrorStore();
    const { reset: resetPopup } = usePopupStore();

    if (!isExpiredSession) {
      return <></>;
    }

    return (
      <MessageDialog
        type="alert"
        title={t('AutoLogout.001', '자동 로그아웃 안내')}
        open={true}
        okBtnLabel={t('AutoLogout.008', '확인')}
        onConfirm={async () => {
          try {
            await doLogout({
              onLogout: () => {},
            }); 
          }finally {
            goLogin({ isGoPreLogin: true }); // 프리로그인 이동
            sessionTimerManager.clearTimerStore(); // 인뱅 sessionTimer Store 클리어
            errorClear(); // error Store 클리어
            resetPopup(); // popup Store 클리어
          }
          
        }}
      >
        <div className="text-center">
          <p className="body-lg text-basic-02">
            <Trans t={t} i18nKey="AutoLogout.007">
              안전한 금융거래를 위해
              <br />
              로그아웃됐어요.
            </Trans>
          </p>
        </div>
      </MessageDialog>
    );
  },
  (prev, next) => {
    return prev.isExpiredSession === next.isExpiredSession;
  },
);
